import React, { useContext, useState } from "react";
import Swal from "sweetalert2";
import LoadingIcon from "../../images/Loading_icon.gif";
import { UrlContext } from "../../contexts/UrlContext";
import { UserContext } from "../../contexts/UserContext";
import { ensureConnectionAndExecute } from "../../utils/eph-connection-manager";
import { pollTaskStatus } from "../../utils/taskStatus";

export default function LogInmail({
  expert_id,
  project_id,
  setExContactedBy,
  setExContactedMethod,
  setExDateCreated,
  backupData,
  setBackupData,
  doRefresh,
  setDoRefresh,
  currentExpertID,
  setCurrentExpertID,
  profileURL,
}) {
  const {
    userData,
    userError,
    adminUser,
    msalAccount,
    notifications,
    setNotifications,
  } = useContext(UserContext);
  const [loadingPopup, setLoadingPopup] = useState(false);
  const { API_URL, URI } = useContext(UrlContext);

  function decodeJwt(token) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (err) {
      console.error("❌ Failed to decode token:", err);
      return null;
    }
  }

  const handleInmail = async () => {
    setLoadingPopup(true);
    const url = `${URI}helper/send-inmail`;
    const ephToken = localStorage.getItem("eph-token");
    const token = JSON.parse(ephToken).value;

    const decodedToken = decodeJwt(token);

    try {
      const result = await ensureConnectionAndExecute(
        "send_inmail",
        async () => {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-access-token": token,
            },
            body: JSON.stringify({
              token: decodedToken?.preferred_username,
              profile_url: profileURL,
              projID: project_id,
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          return await response.json();
        }
      );

      if (!result) {
        throw new Error("No response received from server");
      }

      if (result.status === "exception") {
        setExContactedBy(result.lastContact.contacted_by);
        setExContactedMethod(result.lastContact.mail_inmail);
        setExDateCreated(result.lastContact.Date_Created);
        setBackupData(
          backupData.map((item) => {
            if (item.expert.ID === expert_id) {
              item.expert.Date_Created = result.lastContact.Date_Created;
              item.expert.contacted_by = result.lastContact.contacted_by;
              item.expert.mail_inmail = result.lastContact.mail_inmail;
            }
            return item;
          })
        );
        Swal.fire({
          icon: "info",
          title: "Error!",
          text: result.msg,
        });
        return;
      }

      if (result.status === "error") {
        if (result.message === "User not connected") {
          Swal.fire({
            icon: "error",
            title: "User not connected!",
            text: "Please refresh the page and update the project list on the extension!",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: result.message || "Something went wrong!",
          });
        }
        return;
      }

      if (result.status === "queued") {
        const finalState = await pollTaskStatus(URI, result.task_id, token);
        if (finalState.status === "success") {
          setCurrentExpertID(expert_id);
          setDoRefresh(!doRefresh);
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Inmail request processed successfully!",
          });
        } else if (finalState.status === "timeout") {
          Swal.fire({
            icon: "warning",
            title: "Timeout",
            text: "The extension did not confirm completion in time. Please verify in the extension and try again.",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Inmail failed",
            text: finalState.error || "The extension reported an error while sending the Inmail.",
          });
        }
        return;
      }

      if (result.status === "success") {
        setCurrentExpertID(expert_id);
        setDoRefresh(!doRefresh);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Inmail request processed successfully!",
        });
        return;
      }

      Swal.fire({
        icon: "warning",
        title: "Unexpected response",
        text: "Received an unexpected status from the server.",
      });
    } catch (error) {
      console.error("❌ Error while sending inmail:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.message || "Something went wrong!",
      });
    } finally {
      setLoadingPopup(false);
    }
  };

  const handleConnect = async () => {
    setLoadingPopup(true);
    const url = `${URI}helper/send-connect`;
    const ephToken = localStorage.getItem("eph-token");
    const token = JSON.parse(ephToken).value;

    const decodedToken = decodeJwt(token);

    try {
      const result = await ensureConnectionAndExecute(
        "send_connect",
        async () => {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-access-token": token,
            },
            body: JSON.stringify({
              token: decodedToken?.preferred_username,
              profile_url: profileURL,
              projID: project_id,
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          return await response.json();
        }
      );

      if (!result) {
        throw new Error("No response received from server");
      }

      if (result.status === "exception") {
        setExContactedBy(result.lastContact?.contacted_by);
        setExContactedMethod(result.lastContact?.mail_inmail);
        setExDateCreated(result.lastContact?.Date_Created);
        setBackupData(
          backupData.map((item) => {
            if (item.expert.ID === expert_id) {
              item.expert.Date_Created = result.lastContact?.Date_Created;
              item.expert.contacted_by = result.lastContact?.contacted_by;
              item.expert.mail_inmail = result.lastContact?.mail_inmail;
            }
            return item;
          })
        );
        Swal.fire({
          icon: "info",
          title: "Error!",
          text: result.msg,
        });
        return;
      }

      if (result.status === "error") {
        if (result.message === "User not connected") {
          Swal.fire({
            icon: "error",
            title: "User not connected!",
            text: "Please refresh the page and update the project list on the extension!",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: result.message || "Something went wrong!",
          });
        }
        return;
      }

      const handleConnectSuccess = () => {
        setCurrentExpertID(expert_id);
        setDoRefresh(!doRefresh);
        setExContactedMethod("Connection");
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Connect request processed successfully!",
        });
      };

      if (result.status === "queued") {
        const finalState = await pollTaskStatus(URI, result.task_id, token);
        if (finalState.status === "success") {
          handleConnectSuccess();
        } else if (finalState.status === "timeout") {
          Swal.fire({
            icon: "warning",
            title: "Timeout",
            text: "The extension did not confirm completion in time. Please verify in the extension and try again.",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Connect failed",
            text: finalState.error || "The extension reported an error while sending the connect request.",
          });
        }
        return;
      }

      if (result.status === "success") {
        handleConnectSuccess();
        return;
      }

      Swal.fire({
        icon: "warning",
        title: "Unexpected response",
        text: "Received an unexpected status from the server.",
      });
    } catch (error) {
      console.error("❌ Error while sending connect:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.message || "Something went wrong!",
      });
    } finally {
      setLoadingPopup(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", gap: "5px" }}>
        <button
          className="btn"
          style={{ width: "60px" }}
          onClick={() => handleInmail()}
        >
          <i className="inmail" title="Click to Send Inmail" /> Inmail
        </button>
        <button
          className="btn"
          style={{ width: "60px" }}
          onClick={() => handleConnect()}
        >
          <i className="fa fa-reply" title="Click to Send Connect request" />
        </button>
      </div>

      {loadingPopup && (
        <div className="deletePrj-popup-wrapper">
          <div className="tab-pane fade show deletePrj-div">
            <center>
              <p style={{ fontSize: "18px" }}>Please wait . . .</p>
              <br />
              <img
                src={LoadingIcon}
                alt=""
                style={{ width: "50px", marginRight: "5px" }}
              />
            </center>
          </div>
        </div>
      )}
    </>
  );
}
